import users from './fixtures/users';
import nfts, { nftsById } from './fixtures/nfts';
import sales, { salesById } from './fixtures/sales';
import banner from './fixtures/banner';
import {
  clone,
  paginate,
  parseStringFilter,
  searchItems,
  sortItems,
  wrapResponse,
} from './mockHelpers';

const stripSaleNft = (sale) => {
  const copy = clone(sale);
  delete copy.nft;
  return copy;
};

const attachSaleToNft = (nft) => {
  const copy = clone(nft);
  if (copy.sale) {
    copy.sale = stripSaleNft(salesById[copy.sale._id] || copy.sale);
  }
  return copy;
};

const filterNfts = (query = {}) => {
  let items = nfts.map(attachSaleToNft);

  const owner = parseStringFilter(query.owner);
  if (owner) {
    items = items.filter((item) => item.owner?.wallet?.toLowerCase() === owner);
  }

  const creator = parseStringFilter(query.creator);
  if (creator) {
    items = items.filter((item) => item.creator?.toLowerCase() === creator);
  }

  if (query.status) {
    items = items.filter((item) => item.sale?.status === query.status);
  }

  items = searchItems(items, query.q, [
    (item) => item.title,
    (item) => item.description,
    (item) => item.category,
    (item) => item.owner?.name,
  ]);

  return sortItems(items, query.sort || '-createdAt');
};

const filterSales = (query = {}) => {
  let items = sales.map((sale) => clone(sale));

  if (query.type) {
    items = items.filter((item) => item.type === query.type);
  }

  if (query.status) {
    items = items.filter((item) => item.status === query.status);
  }

  const seller = parseStringFilter(query.seller);
  if (seller) {
    items = items.filter((item) => item.seller?.toLowerCase() === seller);
  }

  items = searchItems(items, query.q, [
    (item) => item.nft?.title,
    (item) => item.nft?.description,
    (item) => item.nft?.category,
  ]);

  return sortItems(
    items.map((item) => ({ ...item, createdAt: item.createdAt || item.nft?.createdAt })),
    query.sort || '-createdAt'
  );
};

const filterUsers = (query = {}) => {
  let items = clone(users);

  items = searchItems(items, query.q, [
    (item) => item.name,
    (item) => item.bio,
    (item) => item.wallet,
  ]);

  return sortItems(items, query.sort || '-createdAt');
};

export const getNFTs = (query = {}) => {
  const filtered = filterNfts(query);
  return wrapResponse(paginate(filtered, query.pageNumber, query.pageSize));
};

export const getSingleNFT = (nftId) => {
  const nft = nftsById[nftId];
  if (!nft) {
    return Promise.reject({ message: 'NFT not found' });
  }

  const result = attachSaleToNft(nft);
  if (result.sale?._id && salesById[result.sale._id]) {
    result.sale = stripSaleNft(salesById[result.sale._id]);
  }

  return wrapResponse(clone(result));
};

export const liveSales = (query = {}) => {
  const filtered = filterSales(query);
  return wrapResponse(paginate(filtered, query.pageNumber, query.pageSize));
};

export const liveAuctions = (query = {}) => {
  return liveSales({ ...query, type: 'auction' });
};

export const singleLiveSale = (saleId) => {
  const sale = salesById[saleId];
  if (!sale) {
    return Promise.reject({ message: 'Sale not found' });
  }

  return wrapResponse(clone(sale));
};

export const getAllUsers = (query = {}) => {
  const filtered = filterUsers(query);
  return wrapResponse(paginate(filtered, query.pageNumber, query.pageSize));
};

export const getSingleUser = (userId) => {
  const user = users.find((item) => item._id === userId);
  if (!user) {
    return Promise.reject({ message: 'User not found' });
  }

  return wrapResponse(clone(user));
};

export const getBanner = () => wrapResponse(clone(banner));
